import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Catalog, CatalogEntry } from '@cemex-core/cmx-api';
import { ListPumpPen } from '@cemex-core/cmx-api/models/calendars/calendar';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Store } from '@ngrx/store';
import { getFirstCalendar, getSpecificationForm } from '@ordersnproducts/modules/order-builder/reducers';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { distinctUntilChangedDeep } from '@ordersnproducts/modules/shared/utils/distinct-until-changed-deep';
import { Features } from '@ordersnproducts/features';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CATALOG_CODE, CatalogCodeType } from '../../../../../shared/constants/catalog-codes';
import {
  BOOM_PUMP,
  BOOM_PUMP_CODE,
  STATIONARY_PUMP,
  STATIONARY_PUMP_CODE,
} from '../../../rms-specification/constants/pumps';
import { SpecFieldNames } from '../../constants/specification-field-names.enum';
import { SpecificationFormState } from '../../reducers/specification/specification.reducer';
import { AbstractCatalogOptionLoader } from './catalog-option-loader.service';
import { TranslateService } from '@ngx-translate/core';

const pumpSizes = {
  specificSizeCode: '5',
  anySizeCode: '92',
};

/**
 * This service includes api pumps (Boom pump and Stationary pump)
 */
@Injectable()
export class PumpApiOptionLoader extends AbstractCatalogOptionLoader {
  constructor(
    protected store: Store<SpecificationFormState>,
    protected featureToggleService: FeatureToggleService,
    private translate: TranslateService,
  ) {
    super(store, featureToggleService);
  }

  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    let preselectDefaultOption = false;

    // Catalog values can be preselected for some countries based on feature toggles
    if (this.featureToggleService.isFeatureEnabled(Features.LoadDefaultCatalogValues)) {
      preselectDefaultOption = true;
    }

    if (this.isBoomPump(formGroup)) {
      // Get options from api repsonse (store)
      return this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar),
        take(1),
        map((calendar) => {
          const placingBoomPump =
            calendar && calendar.pump.unloadMethods.find((method) => method.unloadMethodCode === BOOM_PUMP_CODE);
          return (
            placingBoomPump &&
            placingBoomPump.listPumpPen.map((item) => {
              return {
                id: item.pumpSizeCode,
                name: this.makeDescription(item),
              };
            })
          );
        }),
      );
    }

    if (this.isStationaryPump(formGroup)) {
      // Get options from api repsonse (store)
      return this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar && !!calendar.pump),
        take(1),
        map((calendar) => {
          const stationaryPump = calendar.pump.unloadMethods.find(
            (method) => method.unloadMethodCode === STATIONARY_PUMP_CODE,
          );
          return stationaryPump.listPumpStationary.map((item) => {
            return {
              id: item.pumpSizeCode,
              name: item.vehicleSize,
            };
          });
        }),
      );
    }

    // Hadle standard case
    return this.store.select(getSpecificationForm).pipe(
      distinctUntilChangedDeep(),
      map((state: SpecificationFormState) => {
        return state.catalogs.find((catalog) => {
          return catalog.catalogCode === this.getCatalogType();
        });
      }),
      filter((catalog) => catalog !== undefined),
      map((catalog: Catalog) => catalog.entries),
      map((entries: CatalogEntry[]) => {
        return entries.map((entry) => {
          return {
            id: entry.entryCode,
            name: entry.entryDesc,
            default: preselectDefaultOption ? entry.entryDefault : false,
          };
        });
      }),
    );
  }

  isBoomPump(formGroup: UntypedFormGroup) {
    const field = formGroup.get(SpecFieldNames.UNLOAD_TYPE);
    return field && field.value === BOOM_PUMP;
  }

  isStationaryPump(formGroup: UntypedFormGroup) {
    const field = formGroup.get(SpecFieldNames.UNLOAD_TYPE);
    return field && field.value === STATIONARY_PUMP;
  }

  getCatalogType(): CatalogCodeType {
    return CATALOG_CODE.PUMP_CAPACITY;
  }

  makeDescription(listPumpPen: ListPumpPen): string {
    const pumpCodes = [pumpSizes.anySizeCode, pumpSizes.specificSizeCode];
    // Show pump length just when is not specific size and any size
    if (pumpCodes.includes(listPumpPen.pumpSizeCode)) {
      return `${listPumpPen.vehicleSize}`;
    } else if (listPumpPen.longMin === 0) {
      return `${listPumpPen.vehicleSize} (${listPumpPen.longMax} ${this.translate.instant('views.common.pump_size_unit')})`;
    } else {
      /* tslint:disable:max-line-length */
      return `${listPumpPen.vehicleSize} (${listPumpPen.longMin}-${listPumpPen.longMax} ${this.translate.instant('views.common.pump_size_unit')})`;
    }
  }
}
